import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, RefreshCw, Check, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/authContext";
import { initialsFor } from "@/lib/email/format";
import { MyAccountsSettingsDialog } from "@/components/custom/MyAccountsSettingsDialog";
import {
  api,
  type FirmStalledGroup,
  type FirmStalledResponse,
  type MyBookAccount,
  type MyBookProject,
  type MyBookResponse,
} from "@/lib/api";
import {
  bookTotals,
  daysSince,
  deriveAccount,
  groupFirmByManager,
  pickHeadlineProject,
  relativeDays,
  stalledSeverity,
  unreadAgeSeverity,
  GOING_QUIET_DAYS,
  STALL_SEVERE_DAYS,
  WAITING_ON_CLIENT_AGING_DAYS,
  INBOX_UNREAD_AMBER_DAYS,
  INBOX_UNREAD_RED_DAYS,
  type AccountDerived,
  type ManagerBucket,
} from "@/lib/myBookDerive";

/* ── shared bits ──────────────────────────────────────────────────────────── */

const PILL =
  "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap";
const PILL_TONE = {
  amber: "bg-amber-100 text-amber-800",
  rose: "bg-rose-100 text-rose-700",
  violet: "bg-violet-100 text-violet-700",
  emerald: "bg-emerald-100 text-emerald-700",
} as const;

/** stalledSeverity speaks in "amber"/"red"; the house pill palette calls red "rose". */
const SEVERITY_TONE: Record<"amber" | "red", keyof typeof PILL_TONE> = { amber: "amber", red: "rose" };

/** unreadAgeSeverity speaks in green/amber/red; the pill palette calls those emerald/amber/rose. */
const UNREAD_TONE: Record<"green" | "amber" | "red", keyof typeof PILL_TONE> = {
  green: "emerald",
  amber: "amber",
  red: "rose",
};
const UNREAD_DOT: Record<"green" | "amber" | "red", string> = {
  green: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-rose-500",
};

const COL_LABEL = "text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5";
const LINE = "flex items-center gap-1.5 text-[12.5px] leading-5 min-w-0";

/** Books-currency dot. Mirrors the financial-source status vocabulary. */
const STATUS_DOT: Record<"ok" | "attention" | "overdue", string> = {
  ok: "bg-emerald-500",
  attention: "bg-amber-500",
  overdue: "bg-rose-500",
};

function plural(n: number, one: string, many = `${one}s`): string {
  return n === 1 ? one : many;
}

/** "sms" is an acronym; the other two read better lower-case beside the date. */
const TOUCH_KIND: Record<"email" | "sms" | "meeting", string> = {
  email: "email",
  sms: "SMS",
  meeting: "meeting",
};

/**
 * A bare YYYY-MM-DD is parsed at UTC midnight, which renders as the previous day
 * in Eastern time — the repo's standing date gotcha. Pin such values to noon.
 */
function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

/** "Monthly Bookkeeping — Jul 2026", the app's canonical project label shape. */
function projectLabel(p: Pick<MyBookProject, "title" | "period_display">): string {
  return p.period_display ? `${p.title} — ${p.period_display}` : p.title;
}

function InitialsChip({ name, tone = "muted" }: { name: string | null; tone?: "muted" | "primary" }) {
  return (
    <span
      title={name ?? undefined}
      className={cn(
        "inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[9px] font-bold",
        tone === "primary" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground",
      )}
    >
      {initialsFor(name)}
    </span>
  );
}

function SummaryTile({
  count,
  label,
  tone,
}: {
  count: number;
  label: string;
  tone: "amber" | "rose" | "violet" | "neutral";
}) {
  const countClass =
    tone === "amber"
      ? "text-amber-600"
      : tone === "rose"
        ? "text-rose-700"
        : tone === "violet"
          ? "text-violet-700"
          : "text-foreground";
  return (
    <div
      className="flex min-w-[170px] flex-1 items-center gap-3 rounded-lg border border-border bg-card px-4 py-3"
      data-testid={`my-accounts-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className={cn("text-2xl font-bold", countClass)}>{count}</span>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  );
}

/**
 * A signal that failed server-side and is showing its fallback. Only reachable for
 * `last_activity`, whose fallback marks every project stalled — which looks exactly like a
 * neglected book, so the page has to say which one it is looking at.
 */
function DegradedBanner({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

function ErrorPanel({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-destructive">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        {message}
      </div>
      <Button variant="outline" size="sm" onClick={onRetry}>
        <RefreshCw className="mr-2 h-3.5 w-3.5" />
        Retry
      </Button>
    </div>
  );
}

/* ── health card ──────────────────────────────────────────────────────────── */

function WorkColumn({
  account,
  derived,
  severeDays,
  onOpenProject,
}: {
  account: MyBookAccount;
  derived: AccountDerived;
  /** Firm-tuned amber→rose cut from display_thresholds (fallback: STALL_SEVERE_DAYS). */
  severeDays: number;
  onOpenProject: (project: MyBookProject) => void;
}) {
  const projects = account.projects ?? [];
  const headline = pickHeadlineProject(projects);
  const worst = derived.worstStalledDaysIdle;
  const stalledTone = SEVERITY_TONE[stalledSeverity(worst, severeDays)];

  // Stalled first (nobody is moving it), then merely-overdue. Capped so one bad
  // client can't push the rest of the book off the screen.
  const flagged = [...projects.filter((p) => p.stalled), ...projects.filter((p) => p.overdue && !p.stalled)];
  const shown = flagged.slice(0, 2);
  const extra = flagged.length - shown.length;

  const noFlags = derived.overdueCount === 0 && derived.stalledCount === 0 && derived.reviewsForMe === 0;

  return (
    <div className="min-w-0">
      <div className={COL_LABEL}>Work</div>
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-1.5 text-[12.5px] leading-5">
          {derived.openCount === 0 ? (
            <span className="text-muted-foreground">No open projects</span>
          ) : (
            <span className="font-semibold">
              {derived.openCount} open
            </span>
          )}
          {derived.stalledCount > 0 && (
            <span className={cn(PILL, PILL_TONE[stalledTone])}>
              {worst === null ? "Stalled · no activity recorded" : `Stalled · ${worst}d idle`}
            </span>
          )}
          {derived.overdueCount > 0 && (
            <span className={cn(PILL, PILL_TONE.rose)}>{derived.overdueCount} overdue</span>
          )}
          {derived.reviewsForMe > 0 && (
            <span className={cn(PILL, PILL_TONE.violet)}>
              {derived.reviewsForMe} {plural(derived.reviewsForMe, "review")} waiting on you
            </span>
          )}
          {derived.openCount > 0 && noFlags && <span className="text-muted-foreground">· on schedule</span>}
        </div>

        {/* The step, not the project — the tooltip names the project it belongs to,
            since a card with several open projects can't say so inline. */}
        {headline?.current_step_title && (
          <div className={LINE} title={`${projectLabel(headline)} — next step`}>
            <span className="shrink-0 text-muted-foreground">Next:</span>
            <span className="truncate">{headline.current_step_title}</span>
            {headline.current_assignee_name ? (
              <InitialsChip name={headline.current_assignee_name} />
            ) : (
              <span className="shrink-0 text-muted-foreground">· unassigned</span>
            )}
          </div>
        )}

        {shown.map((p) => (
          <button
            key={p.project_id}
            type="button"
            onClick={() => onOpenProject(p)}
            className={cn(
              LINE,
              "w-full rounded text-left hover:underline",
              p.stalled ? "text-muted-foreground" : "text-rose-700",
            )}
            title="Open this project"
          >
            <span className="truncate">
              {projectLabel(p)}
              {p.stalled
                ? p.last_activity_at
                  ? ` · no activity since ${formatDate(p.last_activity_at)}`
                  : " · no activity recorded"
                : p.due_date
                  ? ` · due ${formatDate(p.due_date)}`
                  : " · overdue"}
            </span>
          </button>
        ))}
        {extra > 0 && <div className="text-[12.5px] leading-5 text-muted-foreground">+{extra} more</div>}
      </div>
    </div>
  );
}

function BooksColumn({ books }: { books: MyBookAccount["books"] }) {
  const { sources_total, sources_by_status, worst_status, min_through_date, latest_closed_bookkeeping } = books;
  const behind = worst_status === "overdue" ? daysSince(min_through_date) : null;

  return (
    <div className="min-w-0">
      <div className={COL_LABEL}>Books</div>
      <div className="flex flex-col gap-1.5">
        {worst_status === null || sources_total === 0 ? (
          <div className={cn(LINE, "text-muted-foreground")}>No sources configured</div>
        ) : (
          <>
            <div className={LINE}>
              <span className={cn("inline-block h-2 w-2 shrink-0 rounded-full", STATUS_DOT[worst_status])} />
              <span className={cn("font-semibold truncate", worst_status === "overdue" && "text-rose-700")}>
                {min_through_date ? `Through ${formatDate(min_through_date)}` : "No through date recorded"}
              </span>
              {behind !== null && behind > 0 && (
                <span className="shrink-0 text-rose-700">· {behind}d behind</span>
              )}
            </div>
            <div className={LINE}>
              {sources_by_status.overdue > 0 ? (
                <>
                  <span className="text-rose-700">
                    {sources_by_status.overdue} {plural(sources_by_status.overdue, "source")} overdue
                  </span>
                  {sources_by_status.ok > 0 && (
                    <span className="text-muted-foreground">· {sources_by_status.ok} current</span>
                  )}
                </>
              ) : sources_by_status.attention > 0 ? (
                <span className="text-amber-700">
                  {sources_by_status.attention} of {sources_total} {plural(sources_total, "source")} need attention
                </span>
              ) : (
                <span className="text-muted-foreground">
                  {sources_total} {plural(sources_total, "source")} · all current
                </span>
              )}
            </div>
          </>
        )}

        <div className={LINE}>
          {latest_closed_bookkeeping ? (
            <>
              <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" strokeWidth={3} />
              <span className="truncate text-muted-foreground">
                Closed: {projectLabel(latest_closed_bookkeeping)}
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">No closed bookkeeping yet</span>
          )}
        </div>
      </div>
    </div>
  );
}

function RelationshipColumn({
  relationship,
  sharedInbox,
  quietDays,
  agingDays,
  unreadAmberDays,
  unreadRedDays,
  onOpenInbox,
}: {
  relationship: MyBookAccount["relationship"];
  /** Unread rollup for the client's mapped shared mailbox(es); null/absent hides the bubble. */
  sharedInbox: MyBookAccount["shared_inbox"];
  /** Firm-tuned going-quiet window from display_thresholds (fallback: GOING_QUIET_DAYS). */
  quietDays: number;
  /** Firm-tuned waiting-on-client aging cut (fallback: WAITING_ON_CLIENT_AGING_DAYS). */
  agingDays: number;
  /** Firm-tuned unread-bubble age cuts (fallbacks: INBOX_UNREAD_AMBER/RED_DAYS). */
  unreadAmberDays: number;
  unreadRedDays: number;
  /** Opens the client's Shared Inbox tab — the bubble is actionable, not just a number. */
  onOpenInbox: () => void;
}) {
  const { last_touch, waiting_on_client } = relationship;
  const touchDays = daysSince(last_touch?.at ?? null);
  const quiet = touchDays !== null && touchDays >= quietDays;
  const oldestDays = daysSince(waiting_on_client?.oldest_follow_up_at ?? null);
  const aging = oldestDays !== null && oldestDays > agingDays;
  const waitingCount = waiting_on_client?.count ?? 0;
  const unreadCount = sharedInbox?.unread_count ?? 0;
  const unreadOldestDays = daysSince(sharedInbox?.oldest_unread_at ?? null);
  const unreadTone = unreadAgeSeverity(unreadOldestDays, unreadAmberDays, unreadRedDays);

  return (
    <div className="min-w-0">
      <div className={COL_LABEL}>Relationship</div>
      <div className="flex flex-col gap-1.5">
        <div className={LINE}>
          {!last_touch ? (
            <span className="text-muted-foreground">No contact recorded</span>
          ) : quiet ? (
            // The pill trades the channel for the warning; the tooltip keeps it.
            <span
              className={cn(PILL, PILL_TONE.amber)}
              title={`Last ${TOUCH_KIND[last_touch.kind] ?? last_touch.kind} ${formatDate(last_touch.at)}`}
            >
              Last touch {relativeDays(last_touch.at)} · going quiet
            </span>
          ) : (
            <>
              <span className="font-semibold">Last touch {relativeDays(last_touch.at)}</span>
              <span className="text-muted-foreground">
                · {TOUCH_KIND[last_touch.kind] ?? last_touch.kind}
              </span>
            </>
          )}
        </div>
        <div className={LINE}>
          <span className="shrink-0 text-muted-foreground">Waiting on client:</span>
          {waitingCount === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className={cn(aging && "font-semibold text-amber-700")}>
              {waitingCount} {plural(waitingCount, "item")}
              {oldestDays !== null ? ` · oldest ${oldestDays}d` : ""}
            </span>
          )}
        </div>
        {/* Unread shared-inbox bubble (v1.5.65). Rendered only when there IS unread mail in a
            mapped shared mailbox — zero unread and "no shared inbox" both show nothing, by
            design (the server's rollup omits both cases, so the client can't tell them apart
            and doesn't need to). Clicking lands on the client's Shared Inbox tab. */}
        {unreadCount > 0 && (
          <div className={LINE}>
            <button
              type="button"
              onClick={onOpenInbox}
              data-testid="my-accounts-unread-bubble"
              title={
                sharedInbox?.oldest_unread_at
                  ? `Oldest unread ${formatDate(sharedInbox.oldest_unread_at)} — open the Shared Inbox tab`
                  : "Open the Shared Inbox tab"
              }
              className={cn(PILL, PILL_TONE[UNREAD_TONE[unreadTone]], "hover:opacity-80")}
            >
              <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", UNREAD_DOT[unreadTone])} />
              {unreadCount} unread
              {unreadOldestDays !== null ? ` · oldest ${unreadOldestDays}d` : ""}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function HealthCard({
  account,
  derived,
  severeDays,
  quietDays,
  agingDays,
  unreadAmberDays,
  unreadRedDays,
  onOpenAccount,
  onOpenProject,
  onOpenInbox,
}: {
  account: MyBookAccount;
  derived: AccountDerived;
  severeDays: number;
  quietDays: number;
  agingDays: number;
  unreadAmberDays: number;
  unreadRedDays: number;
  onOpenAccount: (account: MyBookAccount) => void;
  onOpenProject: (account: MyBookAccount, project: MyBookProject) => void;
  onOpenInbox: (account: MyBookAccount) => void;
}) {
  return (
    <div
      className="grid grid-cols-1 gap-5 rounded-lg border border-border bg-card px-4 py-3.5 lg:grid-cols-[250px_1fr_1fr_1fr]"
      data-testid="my-accounts-card"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <button
          type="button"
          onClick={() => onOpenAccount(account)}
          className="truncate text-left text-base font-semibold hover:underline"
        >
          {account.account_name}
        </button>
        <div className="truncate text-xs text-muted-foreground">{account.entity_type || "—"}</div>
        <button
          type="button"
          onClick={() => onOpenAccount(account)}
          className="mt-1.5 w-fit text-xs font-medium text-primary hover:underline"
        >
          View account →
        </button>
      </div>

      <WorkColumn
        account={account}
        derived={derived}
        severeDays={severeDays}
        onOpenProject={(p) => onOpenProject(account, p)}
      />
      <BooksColumn books={account.books} />
      <RelationshipColumn
        relationship={account.relationship}
        sharedInbox={account.shared_inbox ?? null}
        quietDays={quietDays}
        agingDays={agingDays}
        unreadAmberDays={unreadAmberDays}
        unreadRedDays={unreadRedDays}
        onOpenInbox={() => onOpenInbox(account)}
      />
    </div>
  );
}

/* ── firm tab ─────────────────────────────────────────────────────────────── */

const FIRM_GRID = "grid grid-cols-[220px_minmax(0,1fr)_110px_minmax(0,1fr)_150px] gap-4";

function FirmTable({
  buckets,
  thresholdDays,
  severeDays,
  onOpenProject,
}: {
  buckets: ManagerBucket<FirmStalledGroup>[];
  thresholdDays: number;
  /** Firm-tuned amber→rose cut from the FIRM response's display_thresholds. */
  severeDays: number;
  onOpenProject: (group: FirmStalledGroup, project: MyBookProject) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <div className="min-w-[900px]">
          <div
            className={cn(
              FIRM_GRID,
              "px-4 py-2.5 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground",
            )}
          >
            <span>Client</span>
            <span>Project</span>
            <span>Idle</span>
            <span>Current step</span>
            <span>Holder</span>
          </div>

          {buckets.map((bucket) => (
            <div key={bucket.key}>
              <div className="flex items-center gap-2.5 border-t border-border bg-muted px-4 py-2 text-xs font-bold">
                {bucket.manager ? (
                  <InitialsChip name={bucket.manager.name} tone="primary" />
                ) : (
                  <span className="inline-block h-2 w-2 shrink-0 rounded-full bg-rose-500" />
                )}
                <span>{bucket.manager?.name ?? "No account manager"}</span>
                <span className="font-medium text-muted-foreground">· {bucket.rows.length} stalled</span>
              </div>

              {bucket.rows.map(({ group, project: p }) => (
                <div
                  key={p.project_id}
                  className={cn(FIRM_GRID, "items-center border-t border-border/60 px-4 py-2.5 text-[12.5px]")}
                >
                  <span className="truncate font-semibold">{group.account_name}</span>
                  <button
                    type="button"
                    onClick={() => onOpenProject(group, p)}
                    className="truncate text-left hover:underline"
                    title="Open this project"
                  >
                    {projectLabel(p)}
                  </button>
                  <span>
                    <span className={cn(PILL, PILL_TONE[SEVERITY_TONE[stalledSeverity(p.days_idle, severeDays)]])}>
                      {p.days_idle === null ? "no activity" : `${p.days_idle}d idle`}
                    </span>
                  </span>
                  <span className="truncate text-muted-foreground">{p.current_step_title ?? "—"}</span>
                  {p.current_assignee_name ? (
                    <span className="flex min-w-0 items-center gap-1.5">
                      <InitialsChip name={p.current_assignee_name} />
                      <span className="truncate">{p.current_assignee_name}</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Unassigned</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        A project is stalled when it has had no task completions, comments, time entries, team-chat
        messages, or status changes for {thresholdDays}+ days. Visible to managers and admins.
      </p>
    </div>
  );
}

/* ── page ─────────────────────────────────────────────────────────────────── */

export default function MyAccounts() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const canSeeFirm = user?.role === "manager" || user?.role === "admin";
  const [tab, setTab] = useState<"book" | "firm">("book");

  // Both queries opt out of refetch-on-focus and cut the retry count. Each of these reads
  // fans out over a manager's whole book (per-account bookkeeping + last-touch lookups), so
  // the app-wide defaults — refetch every time the tab regains focus, three retries on
  // failure — turn ordinary alt-tabbing into repeated multi-query storms. Set here rather
  // than on the shared QueryClient, which every other page depends on.
  const bookQuery = useQuery<MyBookResponse>({
    queryKey: ["my-accounts"],
    queryFn: () => api.getMyBook(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const firmQuery = useQuery<FirmStalledResponse>({
    queryKey: ["my-accounts-firm"],
    queryFn: () => api.getFirmStalled(),
    staleTime: 60_000,
    enabled: tab === "firm" && canSeeFirm,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const accounts = bookQuery.data?.accounts ?? [];
  const thresholdDays = bookQuery.data?.threshold_days ?? 7;
  /** The stall column is showing its fallback, so every project below reads as stalled. */
  const stallDegraded = (bookQuery.data?.signals_degraded ?? []).includes("last_activity");

  // Firm-tuned display banding (v1.5.64). A payload without display_thresholds (old cache,
  // pre-upgrade server) falls back to the shipped constants and renders exactly as before.
  const dt = bookQuery.data?.display_thresholds;
  const severeDays = dt?.stall_severe_days ?? STALL_SEVERE_DAYS;
  const quietDays = dt?.going_quiet_days ?? GOING_QUIET_DAYS;
  const agingDays = dt?.waiting_on_client_aging_days ?? WAITING_ON_CLIENT_AGING_DAYS;
  const unreadAmberDays = dt?.inbox_unread_amber_days ?? INBOX_UNREAD_AMBER_DAYS;
  const unreadRedDays = dt?.inbox_unread_red_days ?? INBOX_UNREAD_RED_DAYS;
  // The Firm tab bands with ITS OWN response's value — the two responses are fetched
  // independently and could momentarily disagree right after an admin save.
  const firmSevereDays = firmQuery.data?.display_thresholds?.stall_severe_days ?? STALL_SEVERE_DAYS;

  /**
   * Attention first — a client whose work is stalled or overdue is exactly what
   * this page exists to surface, so alphabetical order would bury it. Within each
   * bucket the order is alphabetical, so it stays predictable between refreshes.
   */
  const rows = useMemo(() => {
    const derivedRows = accounts.map((account) => ({ account, derived: deriveAccount(account, user?.id) }));
    return derivedRows.sort((a, b) => {
      const aFlag = a.derived.stalledCount > 0 || a.derived.overdueCount > 0 ? 0 : 1;
      const bFlag = b.derived.stalledCount > 0 || b.derived.overdueCount > 0 ? 0 : 1;
      return aFlag - bFlag || a.account.account_name.localeCompare(b.account.account_name);
    });
  }, [accounts, user?.id]);

  const totals = useMemo(() => bookTotals(accounts, user?.id), [accounts, user?.id]);

  const firmGroups = firmQuery.data?.groups ?? [];
  const firmBuckets = useMemo(() => groupFirmByManager(firmGroups), [firmQuery.data]);

  const firmProjectCount = firmBuckets.reduce((sum, b) => sum + b.rows.length, 0);
  const firmClientCount = firmGroups.length; // the payload is already one group per client

  const openAccount = (account: MyBookAccount) => navigate(`/accounts/${account.account_id}`);

  /** The unread bubble lands on the client's Shared Inbox tab (the v1.5.30 deep-link). */
  const openInbox = (account: MyBookAccount) =>
    navigate(`/accounts/${account.account_id}?tab=shared-inbox`);

  /** Same sessionStorage handshake AccountDetail uses to focus one project on the Projects page. */
  const openProject = (accountId: string, accountName: string, projectId: string) => {
    try {
      sessionStorage.setItem(
        "projectsNav",
        JSON.stringify({ accountId, accountName, projectId, focusProjectId: projectId }),
      );
    } catch {
      /* private mode / blocked storage — the Projects page just opens unfocused */
    }
    navigate("/projects");
  };

  const refreshActive = () => (tab === "firm" ? firmQuery.refetch() : bookQuery.refetch());

  // Counts are only quoted once the data behind them has arrived — a subtitle
  // reading "0 projects across 0 clients" mid-load is worse than saying nothing.
  const subtitle =
    tab === "firm"
      ? firmQuery.data
        ? `Firm-wide stalled work · ${firmProjectCount} ${plural(firmProjectCount, "project")} across ${firmClientCount} ${plural(firmClientCount, "client")} · stall threshold ${firmQuery.data.threshold_days} days`
        : "Firm-wide stalled work"
      : bookQuery.data
        ? `${accounts.length} ${plural(accounts.length, "client")} · stall threshold ${thresholdDays} days`
        : "Your clients at a glance";

  return (
    <Layout>
      <Tabs value={tab} onValueChange={(v) => setTab(v as "book" | "firm")} className="space-y-4">
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold">My Accounts</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {user?.role === "admin" && <MyAccountsSettingsDialog />}
            <Button
              variant="ghost"
              size="sm"
              onClick={refreshActive}
              disabled={tab === "firm" ? firmQuery.isFetching : bookQuery.isFetching}
              title="Refresh"
            >
              <RefreshCw
                className={cn(
                  "h-4 w-4",
                  (tab === "firm" ? firmQuery.isFetching : bookQuery.isFetching) && "animate-spin",
                )}
              />
            </Button>
            <TabsList>
              <TabsTrigger value="book">My Accounts</TabsTrigger>
              {canSeeFirm && <TabsTrigger value="firm">Firm</TabsTrigger>}
            </TabsList>
          </div>
        </div>

        <TabsContent value="book" className="mt-0 space-y-4">
          {bookQuery.isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : bookQuery.isError ? (
            <ErrorPanel
              message={(bookQuery.error as Error)?.message || "Failed to load My Accounts"}
              onRetry={() => bookQuery.refetch()}
            />
          ) : accounts.length === 0 ? (
            <div className="rounded-lg border border-border bg-card px-6 py-14 text-center">
              <p className="text-base font-medium">You don't manage any clients yet</p>
              <p className="mx-auto mt-1.5 max-w-md text-sm text-muted-foreground">
                Account managers are designated on a client's page under Assignees → workflow role.
              </p>
            </div>
          ) : (
            <>
              {/* Above the tiles as well as the cards — the Stalled tile counts the same
                  fallback, so flagging only the cards would leave a confident wrong number. */}
              {stallDegraded && (
                <DegradedBanner message="Activity data is temporarily unavailable — projects are shown as stalled until it recovers." />
              )}

              <div className="flex flex-wrap gap-3">
                <SummaryTile count={totals.stalled} label="Stalled" tone="amber" />
                <SummaryTile count={totals.overdue} label="Overdue" tone="rose" />
                <SummaryTile count={totals.reviewsForMe} label="Reviews waiting on you" tone="violet" />
                <SummaryTile count={totals.waitingOnClient} label="Waiting on client" tone="neutral" />
              </div>

              <div className="flex flex-col gap-3">
                {rows.map(({ account, derived }) => (
                  <HealthCard
                    key={account.account_id}
                    account={account}
                    derived={derived}
                    severeDays={severeDays}
                    quietDays={quietDays}
                    agingDays={agingDays}
                    unreadAmberDays={unreadAmberDays}
                    unreadRedDays={unreadRedDays}
                    onOpenAccount={openAccount}
                    onOpenProject={(acc, p) =>
                      openProject(acc.account_id, acc.account_name, p.project_id)
                    }
                    onOpenInbox={openInbox}
                  />
                ))}
              </div>
            </>
          )}
        </TabsContent>

        {canSeeFirm && (
          <TabsContent value="firm" className="mt-0 space-y-4">
            {firmQuery.isLoading ? (
              <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : firmQuery.isError ? (
              <ErrorPanel
                message={(firmQuery.error as Error)?.message || "Failed to load firm stalled work"}
                onRetry={() => firmQuery.refetch()}
              />
            ) : firmBuckets.length === 0 ? (
              <div className="rounded-lg border border-border bg-card px-6 py-14 text-center">
                <p className="text-base font-medium">Nothing is stalled firm-wide</p>
                <p className="mx-auto mt-1.5 max-w-md text-sm text-muted-foreground">
                  Every open project has seen activity in the last{" "}
                  {firmQuery.data?.threshold_days ?? thresholdDays} days.
                </p>
              </div>
            ) : (
              <FirmTable
                buckets={firmBuckets}
                thresholdDays={firmQuery.data?.threshold_days ?? thresholdDays}
                severeDays={firmSevereDays}
                onOpenProject={(group, p) => openProject(group.account_id, group.account_name, p.project_id)}
              />
            )}
          </TabsContent>
        )}
      </Tabs>
    </Layout>
  );
}
